// Report cards for bot chats. Each run_report a bot ran arrives as a message
// of kind 'report'. Instead of printing the rows, the card picks a picture
// that fits the shape of the data:
//   one window, no breakdown        → KPI tiles
//   same metrics, several windows   → comparison bars with % change
//   by day / week / month           → line chart
//   by weekday / hour               → column chart
//   by status, aging, type, ...     → donut with shares
//   by team member, service, ...    → ranked horizontal bars
// The formula sits on top like a caption, and the raw rows are one tap away.

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions,
  type TooltipItem,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { ChevronDown, ChevronUp, Table2 } from 'lucide-react';
import type { MessageRow } from './types';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler);

// ── Data ─────────────────────────────────────────────────────────────────────

export interface ReportColumn { name: string; label: string; type: 'money' | 'number' | 'dimension' }

export interface Report {
  id: string;
  formula: string;
  dataset: string;
  since: string | null;
  until: string | null;
  asOf: string | null;
  currency: string;
  columns: ReportColumn[];
  rows: Array<Record<string, unknown>>;
  dimension: ReportColumn | null;
  metrics: ReportColumn[];
}

export type ReportCardSource = Pick<MessageRow, 'id' | 'content' | 'meta'>;

export function parseReport(message: ReportCardSource): Report {
  const meta = (message.meta || {}) as Record<string, unknown>;
  const columns = (Array.isArray(meta.columns) ? meta.columns : []) as ReportColumn[];
  return {
    id: message.id,
    formula: String(meta.formula || message.content || ''),
    dataset: String(meta.dataset || ''),
    since: typeof meta.since === 'string' ? meta.since : null,
    until: typeof meta.until === 'string' ? meta.until : null,
    asOf: typeof meta.as_of === 'string' ? meta.as_of : null,
    currency: typeof meta.currency === 'string' && meta.currency ? meta.currency : 'USD',
    columns,
    rows: (Array.isArray(meta.rows) ? meta.rows : []) as Array<Record<string, unknown>>,
    dimension: columns.find((c) => c.type === 'dimension') || null,
    metrics: columns.filter((c) => c.type !== 'dimension'),
  };
}

const DATASET_LABEL: Record<string, string> = {
  sales: 'Sales',
  scheduled_sales: 'Scheduled sales',
  payments: 'Payments',
  receivables: 'Receivables',
  quotes: 'Quotes',
  customers: 'Customers',
  jobs: 'Jobs',
  expenses: 'Expenses',
  profit: 'Profit',
  website: 'Website',
  social: 'Social',
  leads: 'Leads',
  calls: 'Calls',
  google_business: 'Google Business',
  campaigns: 'Campaigns',
};

const TIME_DIMENSIONS = new Set(['day', 'week', 'month']);
const ORDINAL_DIMENSIONS = new Set(['weekday', 'hour']);
// Breakdowns that read as "share of the whole": a few slices, each a part.
const SHARE_DIMENSIONS = new Set([
  'status', 'payment_state', 'new_or_returning_customer', 'customer_type', 'service_location', 'aging',
  'line_kind', 'channel', 'method', 'payment_type', 'booked_via', 'sale_type', 'conversion_type', 'paid_for',
  'device', 'outcome', 'cost_type', 'linked_to', 'payment_method', 'timing',
]);
// Metrics where going up is bad news.
const LOWER_IS_BETTER = new Set([
  'discounts', 'refunds', 'refund_count', 'cancelled', 'cancellation_rate', 'lost', 'overdue', 'overdue_invoices',
  'outstanding', 'open_invoices', 'customers_owing', 'oldest_overdue_days', 'declined', 'expired', 'owing',
  'expenses', 'tax', 'total_with_tax', 'pending_expenses', 'pending_entries', 'job_expenses', 'payroll',
  'operating_costs', 'total_costs',
]);
// Metrics that cannot be added up across rows.
const NOT_ADDITIVE = /average|_rate$|^margin$|per_customer|oldest|days|engagement_seconds/;

const PALETTE = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#db2777', '#65a30d', '#71717a', '#a1a1aa'];
const INK = '#18181b';
const MUTED = '#71717a';
const GRID = '#e4e4e7';

// ── Theme ────────────────────────────────────────────────────────────────────
// The deck was drawn for the dark bot chat; the Control Centre asks for the
// same cards on white. Every colour class goes through this table.

export type DeckTheme = 'dark' | 'light';

const TOKENS = {
  dark: {
    shell: 'border border-white/10 bg-zinc-900',
    head: 'bg-white/5',
    btnOn: 'bg-white/10 text-zinc-100',
    btnOff: 'text-zinc-400 hover:text-zinc-200',
    chipOn: 'bg-white text-zinc-900',
    chipOff: 'bg-white/10 text-zinc-300 hover:bg-white/15',
    divide: 'divide-white/5',
    row: 'border-t border-white/5 odd:bg-white/[0.02]',
    tile: 'bg-white/5',
    track: 'bg-white/5',
    link: 'text-sky-300',
    good: 'text-emerald-300',
    bad: 'text-rose-300',
    strong: 'text-zinc-100',
    text: 'text-zinc-300',
    muted: 'text-zinc-400',
    donutBorder: '#18181b',
    barMuted: '#a1a1aa',
  },
  light: {
    shell: 'border border-gray-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
    head: 'bg-gray-50',
    btnOn: 'bg-gray-100 text-gray-900',
    btnOff: 'text-gray-500 hover:text-gray-800',
    chipOn: 'bg-gray-900 text-white',
    chipOff: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    divide: 'divide-gray-100',
    row: 'border-t border-gray-100 odd:bg-gray-50/60',
    tile: 'bg-gray-50',
    track: 'bg-gray-100',
    link: 'text-[#1a73e8]',
    good: 'text-green-600',
    bad: 'text-red-500',
    strong: 'text-gray-900',
    text: 'text-gray-700',
    muted: 'text-gray-500',
    donutBorder: '#ffffff',
    barMuted: '#d4d4d8',
  },
} as const;

const DeckThemeContext = createContext<DeckTheme>('dark');

function useTokens() {
  return TOKENS[useContext(DeckThemeContext)];
}

// ── Formatting ───────────────────────────────────────────────────────────────

function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function money(n: number, currency: string, compact = false): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: compact ? 1 : 2,
      minimumFractionDigits: compact ? 0 : 2,
    }).format(n);
  } catch {
    return n.toFixed(compact ? 0 : 2);
  }
}

export function formatValue(column: ReportColumn, value: unknown, currency: string, compact = false): string {
  const n = toNumber(value);
  if (n == null) return value == null || value === '' ? '—' : String(value);
  if (column.type === 'money') return money(n, currency, compact);
  if (/_rate$/.test(column.name)) return `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
  if (compact && Math.abs(n) >= 10000) return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  return n.toLocaleString('en-US', { maximumFractionDigits: Number.isInteger(n) ? 0 : 1 });
}

function parseDate(iso: string | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

const MONTH_LONG = (d: Date) => d.toLocaleDateString('en-US', { month: 'long' });
const MONTH_SHORT = (d: Date) => d.toLocaleDateString('en-US', { month: 'short' });

export function windowLabel(report: Pick<Report, 'since' | 'until' | 'asOf'>, short = false): string {
  const month = short ? MONTH_SHORT : MONTH_LONG;
  const asOf = parseDate(report.asOf);
  if (asOf) return `As of ${month(asOf)} ${asOf.getDate()}, ${asOf.getFullYear()}`;
  const since = parseDate(report.since);
  const until = parseDate(report.until);
  if (!since || !until) return '';
  if (since.getTime() === until.getTime()) return `${month(since)} ${since.getDate()}, ${since.getFullYear()}`;
  if (since.getFullYear() === until.getFullYear()) {
    if (since.getMonth() === until.getMonth()) {
      const whole = since.getDate() === 1 && until.getDate() === new Date(until.getFullYear(), until.getMonth() + 1, 0).getDate();
      return whole ? `${month(since)} ${since.getFullYear()}` : `${month(since)} ${since.getDate()}–${until.getDate()}, ${since.getFullYear()}`;
    }
    return `${month(since)} ${since.getDate()} – ${month(until)} ${until.getDate()}, ${since.getFullYear()}`;
  }
  return `${month(since)} ${since.getDate()}, ${since.getFullYear()} – ${month(until)} ${until.getDate()}, ${until.getFullYear()}`;
}

function timeLabel(dimension: string, raw: string): string {
  const d = parseDate(dimension === 'month' ? `${raw}-01` : raw);
  if (!d) return raw;
  if (dimension === 'month') return `${MONTH_SHORT(d)} ${d.getFullYear()}`;
  return `${MONTH_SHORT(d)} ${d.getDate()}`;
}

function reportTitle(report: Report): string {
  const dataset = DATASET_LABEL[report.dataset] || report.dataset.replace(/_/g, ' ');
  const by = report.dimension ? ` by ${report.dimension.label.toLowerCase()}` : '';
  const when = windowLabel(report);
  return when ? `${dataset}${by} · ${when}` : `${dataset}${by}`;
}

function sumMetric(report: Report, metric: ReportColumn): number | null {
  if (NOT_ADDITIVE.test(metric.name)) return null;
  let total = 0;
  for (const row of report.rows) total += toNumber(row[metric.name]) ?? 0;
  return total;
}

function pctChange(current: number | null, previous: number | null): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function dayCount(report: Pick<Report, 'since' | 'until'>): number {
  const a = parseDate(report.since);
  const b = parseDate(report.until);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

/** The window to compare the latest one against: the earlier one closest in length (Oct 1–22 vs Sep 1–22, not vs all of September). */
function likeForLike(latest: Report, others: Report[]): Report | null {
  if (others.length === 0) return null;
  const target = dayCount(latest);
  return [...others].sort((a, b) => Math.abs(dayCount(a) - target) - Math.abs(dayCount(b) - target) || (b.since || '').localeCompare(a.since || ''))[0];
}

/** `in_progress` → `In progress`; names and dates pass through untouched. */
function humanize(value: string): string {
  if (!/^[a-z][a-z0-9_]*$/.test(value)) return value;
  const words = value.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function DeltaBadge({ pct, metric, against }: { pct: number | null; metric: ReportColumn; against?: string }) {
  const t = useTokens();
  if (pct == null) return null;
  const up = pct >= 0;
  const good = LOWER_IS_BETTER.has(metric.name) ? !up : up;
  const colour = Math.abs(pct) < 0.05 ? t.muted : good ? t.good : t.bad;
  return (
    <span className={`text-[11.5px] font-semibold tabular-nums ${colour}`}>
      {up ? '▲' : '▼'} {Math.abs(pct).toLocaleString('en-US', { maximumFractionDigits: 1 })}%
      {against && <span className={`font-normal ${t.muted}`}> vs {against}</span>}
    </span>
  );
}

/** Days, weeks, or months inside the window with no row get a zero, so a line does not glide over them. */
function fillTimeGaps(report: Report): Array<Record<string, unknown>> {
  const dim = report.dimension;
  const since = parseDate(report.since);
  const until = parseDate(report.until);
  if (!dim || !since || !until || !TIME_DIMENSIONS.has(dim.name)) return report.rows;
  const byKey = new Map(report.rows.map((r) => [String(r[dim.name] ?? ''), r]));
  const pad = (n: number) => String(n).padStart(2, '0');
  const keyOf = (d: Date) => dim.name === 'month'
    ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}`
    : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const zero: Record<string, unknown> = {};
  for (const m of report.metrics) zero[m.name] = 0;
  const out: Array<Record<string, unknown>> = [];
  const cursor = new Date(since);
  if (dim.name === 'week') cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
  if (dim.name === 'month') cursor.setDate(1);
  let guard = 0;
  while (cursor <= until && guard < 2000) {
    const key = keyOf(cursor);
    out.push(byKey.get(key) || { ...zero, [dim.name]: key });
    if (dim.name === 'day') cursor.setDate(cursor.getDate() + 1);
    else if (dim.name === 'week') cursor.setDate(cursor.getDate() + 7);
    else cursor.setMonth(cursor.getMonth() + 1);
    guard += 1;
  }
  return out.length >= report.rows.length ? out : report.rows;
}

// ── Shell ────────────────────────────────────────────────────────────────────

function Shell({ formula, title, table, children }: { formula: string; title: string; table?: ReactNode; children: ReactNode }) {
  const t = useTokens();
  const [open, setOpen] = useState(true);
  const [showTable, setShowTable] = useState(false);
  return (
    <div className={`min-w-0 w-full overflow-hidden rounded-2xl ${t.shell}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className={`flex w-full items-center gap-2 ${t.head} px-3 py-2 text-left`} title={formula}>
        <span className={`min-w-0 flex-1 truncate font-mono text-[11.5px] ${t.text}`}>{formula}</span>
        {open ? <ChevronUp className={`h-3.5 w-3.5 shrink-0 ${t.muted}`} /> : <ChevronDown className={`h-3.5 w-3.5 shrink-0 ${t.muted}`} />}
      </button>
      {open && (
        <div className="px-4 pb-3 pt-3">
          <div className="mb-3 flex items-start justify-between gap-3">
            <p className={`text-[13px] font-semibold ${t.strong}`}>{title}</p>
            {table && (
              <button type="button" onClick={() => setShowTable((v) => !v)} className={`inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] ${showTable ? t.btnOn : t.btnOff}`}>
                <Table2 className="h-3.5 w-3.5" /> Table
              </button>
            )}
          </div>
          {showTable && table ? table : children}
        </div>
      )}
    </div>
  );
}

function MetricChips({ metrics, active, onPick }: { metrics: ReportColumn[]; active: string; onPick: (name: string) => void }) {
  const t = useTokens();
  if (metrics.length < 2) return null;
  return (
    <div className="mb-3 flex flex-wrap gap-1.5">
      {metrics.map((m) => (
        <button
          key={m.name}
          type="button"
          onClick={() => onPick(m.name)}
          className={`rounded-full px-2.5 py-1 text-[11.5px] font-medium ${m.name === active ? t.chipOn : t.chipOff}`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function RowsTable({ report }: { report: Report }) {
  const t = useTokens();
  const [all, setAll] = useState(false);
  const limit = 12;
  const rows = all ? report.rows : report.rows.slice(0, limit);
  if (report.rows.length === 0) return <p className={`py-2 text-[13px] ${t.muted}`}>Nothing recorded for this period.</p>;
  if (!report.dimension) {
    return (
      <div className={`divide-y ${t.divide}`}>
        {report.metrics.map((m) => (
          <div key={m.name} className="flex items-center justify-between gap-4 py-1.5 text-[13px]">
            <span className={`${t.text}`}>{m.label}</span>
            <span className={`font-semibold tabular-nums ${t.strong}`}>{formatValue(m, report.rows[0]?.[m.name], report.currency)}</span>
          </div>
        ))}
      </div>
    );
  }
  const dim = report.dimension;
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full min-w-[280px] border-collapse text-[13px]">
        <thead>
          <tr className={`text-left text-[11.5px] uppercase tracking-wide ${t.muted}`}>
            <th className="px-1 pb-1.5 font-medium">{dim.label}</th>
            {report.metrics.map((m) => <th key={m.name} className="px-1 pb-1.5 text-right font-medium">{m.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${String(row[dim.name])}-${i}`} className={`${t.row}`}>
              <td className={`px-1 py-1.5 ${t.text} [overflow-wrap:anywhere]`}>{humanize(String(row[dim.name] ?? ''))}</td>
              {report.metrics.map((m) => (
                <td key={m.name} className={`px-1 py-1.5 text-right font-semibold tabular-nums ${t.strong}`}>{formatValue(m, row[m.name], report.currency)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {report.rows.length > limit && (
        <button type="button" onClick={() => setAll((v) => !v)} className={`mt-2 px-1 text-[12px] ${t.link} underline-offset-2 hover:underline`}>
          {all ? 'Show fewer' : `Show all ${report.rows.length}`}
        </button>
      )}
    </div>
  );
}

// ── Pictures ─────────────────────────────────────────────────────────────────

function Empty() {
  const t = useTokens();
  return <p className={`py-6 text-center text-[13px] ${t.muted}`}>Nothing recorded for this period.</p>;
}

/** One window, no breakdown: the headline number big, the rest as tiles. */
function KpiCard({ report }: { report: Report }) {
  const t = useTokens();
  const row = report.rows[0] || {};
  const [first, ...rest] = report.metrics;
  if (!first) return <Empty />;
  return (
    <div>
      <div className="mb-3">
        <p className={`text-[12px] ${t.muted}`}>{first.label}</p>
        <p className={`text-[28px] font-semibold leading-tight tabular-nums ${t.strong}`}>{formatValue(first, row[first.name], report.currency)}</p>
      </div>
      {rest.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {rest.map((m) => (
            <div key={m.name} className={`rounded-xl ${t.tile} px-3 py-2`}>
              <p className={`truncate text-[11.5px] ${t.muted}`}>{m.label}</p>
              <p className={`text-[15px] font-semibold tabular-nums ${t.strong}`}>{formatValue(m, row[m.name], report.currency)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Same metrics over several windows: one block per metric, a bar per window. */
function CompareCard({ reports }: { reports: Report[] }) {
  const t = useTokens();
  const base = reports[0];
  const ordered = useMemo(
    () => [...reports].sort((a, b) => (a.since || '').localeCompare(b.since || '') || (a.until || '').localeCompare(b.until || '')),
    [reports],
  );
  const latest = ordered[ordered.length - 1];
  const against = useMemo(() => likeForLike(latest, ordered.slice(0, -1)), [latest, ordered]);
  return (
    <div className="space-y-4">
      {base.metrics.map((metric) => {
        const values = ordered.map((r) => toNumber(r.rows[0]?.[metric.name]));
        const max = Math.max(...values.map((v) => Math.abs(v ?? 0)), 0);
        const latestValue = values[values.length - 1];
        const previous = against ? toNumber(against.rows[0]?.[metric.name]) : null;
        const pct = pctChange(latestValue, previous);
        return (
          <div key={metric.name}>
            <div className="mb-1.5 flex items-baseline justify-between gap-3">
              <p className={`text-[12.5px] font-medium ${t.text}`}>{metric.label}</p>
              <div className="flex items-baseline gap-2">
                <span className={`text-[15px] font-semibold tabular-nums ${t.strong}`}>{formatValue(metric, latestValue, latest.currency)}</span>
                <DeltaBadge pct={pct} metric={metric} against={against ? windowLabel(against, true) : undefined} />
              </div>
            </div>
            <div className="space-y-1.5">
              {ordered.map((r, i) => {
                const v = values[i];
                const width = max > 0 && v != null ? Math.max((Math.abs(v) / max) * 100, 1.5) : 0;
                const isLatest = i === ordered.length - 1;
                return (
                  <div key={r.id} className="flex items-center gap-2 text-[12px]">
                    <span className={`w-[112px] shrink-0 truncate ${t.muted}`} title={windowLabel(r)}>{windowLabel(r, true)}</span>
                    <div className={`h-[18px] min-w-0 flex-1 overflow-hidden rounded-md ${t.track}`}>
                      <div className="h-full rounded-md" style={{ width: `${width}%`, backgroundColor: isLatest ? PALETTE[0] : t.barMuted }} />
                    </div>
                    <span className={`w-[92px] shrink-0 text-right font-semibold tabular-nums ${t.strong}`}>{formatValue(metric, v, r.currency)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function baseOptions(metric: ReportColumn, currency: string): ChartOptions<'bar' | 'line'> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 300 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: INK,
        titleColor: '#fafafa',
        bodyColor: '#fafafa',
        padding: 8,
        displayColors: false,
        callbacks: {
          label: (item: TooltipItem<'bar' | 'line'>) => {
            const horizontal = (item.chart.options as { indexAxis?: string }).indexAxis === 'y';
            return `${metric.label}: ${formatValue(metric, horizontal ? item.parsed.x : item.parsed.y, currency)}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { color: GRID }, ticks: { color: MUTED, font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
      y: {
        beginAtZero: true,
        grid: { color: GRID },
        border: { display: false },
        ticks: { color: MUTED, font: { size: 11 }, maxTicksLimit: 5, callback: (v) => formatValue(metric, v, currency, true) },
      },
    },
  };
}

/** By day, week, or month: a line with the total above it. */
function TimeSeriesCard({ report }: { report: Report }) {
  const t = useTokens();
  const defaultMetric = report.metrics.find((m) => m.type === 'money') || report.metrics[0];
  const [active, setActive] = useState(defaultMetric?.name || '');
  const metric = report.metrics.find((m) => m.name === active) || defaultMetric;
  const rows = useMemo(() => fillTimeGaps(report), [report]);
  if (!metric || report.rows.length === 0) return <Empty />;
  const dim = report.dimension!;
  const labels = rows.map((r) => timeLabel(dim.name, String(r[dim.name] ?? '')));
  const values = rows.map((r) => toNumber(r[metric.name]) ?? 0);
  const total = sumMetric(report, metric);
  const data = {
    labels,
    datasets: [{
      data: values,
      borderColor: PALETTE[0],
      backgroundColor: 'rgba(37, 99, 235, 0.12)',
      fill: true,
      tension: 0.3,
      pointRadius: values.length > 31 ? 0 : 3,
      pointHoverRadius: 5,
      pointBackgroundColor: PALETTE[0],
      borderWidth: 2,
    }],
  };
  const options = baseOptions(metric, report.currency) as ChartOptions<'line'>;
  options.interaction = { mode: 'index', intersect: false };
  return (
    <div>
      <MetricChips metrics={report.metrics} active={metric.name} onPick={setActive} />
      {total != null && (
        <p className={`mb-2 text-[22px] font-semibold leading-tight tabular-nums ${t.strong}`}>
          {formatValue(metric, total, report.currency)} <span className={`text-[12px] font-normal ${t.muted}`}>total</span>
        </p>
      )}
      <div className="h-[190px]"><Line data={data} options={options} /></div>
    </div>
  );
}

/** By weekday or hour: columns in the order they came. */
function OrdinalCard({ report }: { report: Report }) {
  const defaultMetric = report.metrics.find((m) => m.type === 'money') || report.metrics[0];
  const [active, setActive] = useState(defaultMetric?.name || '');
  const metric = report.metrics.find((m) => m.name === active) || defaultMetric;
  if (!metric || report.rows.length === 0) return <Empty />;
  const dim = report.dimension!;
  const labels = report.rows.map((r) => {
    const raw = humanize(String(r[dim.name] ?? ''));
    return dim.name === 'weekday' ? raw.slice(0, 3) : raw;
  });
  const values = report.rows.map((r) => toNumber(r[metric.name]) ?? 0);
  const top = Math.max(...values);
  const data = {
    labels,
    datasets: [{
      data: values,
      backgroundColor: values.map((v) => (v === top ? PALETTE[0] : 'rgba(37, 99, 235, 0.45)')),
      borderRadius: 6,
      maxBarThickness: 36,
    }],
  };
  const options = baseOptions(metric, report.currency) as ChartOptions<'bar'>;
  return (
    <div>
      <MetricChips metrics={report.metrics} active={metric.name} onPick={setActive} />
      <div className="h-[190px]"><Bar data={data} options={options} /></div>
    </div>
  );
}

/** A few slices that make a whole: donut plus a legend with shares. */
function ShareCard({ report }: { report: Report }) {
  const t = useTokens();
  const defaultMetric = report.metrics.find((m) => m.type === 'money') || report.metrics[0];
  const [active, setActive] = useState(defaultMetric?.name || '');
  const metric = report.metrics.find((m) => m.name === active) || defaultMetric;
  if (!metric || report.rows.length === 0) return <Empty />;
  const dim = report.dimension!;
  const slices = report.rows
    .map((r) => ({ label: humanize(String(r[dim.name] ?? '')), value: Math.max(toNumber(r[metric.name]) ?? 0, 0) }))
    .filter((s) => s.value > 0);
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (slices.length === 0 || total === 0) return <Empty />;
  const data = {
    labels: slices.map((s) => s.label),
    datasets: [{ data: slices.map((s) => s.value), backgroundColor: slices.map((_, i) => PALETTE[i % PALETTE.length]), borderWidth: 2, borderColor: t.donutBorder, hoverOffset: 4 }],
  };
  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: INK, titleColor: '#fafafa', bodyColor: '#fafafa', padding: 8, displayColors: false,
        callbacks: { label: (item) => `${item.label}: ${formatValue(metric, item.parsed, report.currency)} (${((Number(item.parsed) / total) * 100).toFixed(1)}%)` },
      },
    },
  };
  return (
    <div>
      <MetricChips metrics={report.metrics} active={metric.name} onPick={setActive} />
      <div className="flex items-center gap-4">
        <div className="relative h-[150px] w-[150px] shrink-0">
          <Doughnut data={data} options={options} />
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-[14px] font-semibold tabular-nums ${t.strong}`}>{formatValue(metric, total, report.currency, true)}</span>
            <span className={`text-[10.5px] ${t.muted}`}>total</span>
          </div>
        </div>
        <ul className="min-w-0 flex-1 space-y-1.5">
          {slices.map((s, i) => (
            <li key={s.label} className="flex items-center gap-2 text-[12.5px]">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
              <span className={`min-w-0 flex-1 truncate ${t.text}`} title={s.label}>{s.label}</span>
              <span className={`shrink-0 tabular-nums ${t.muted}`}>{((s.value / total) * 100).toFixed(0)}%</span>
              <span className={`w-[84px] shrink-0 text-right font-semibold tabular-nums ${t.strong}`}>{formatValue(metric, s.value, report.currency)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Team members, services, customers, sources: ranked horizontal bars. */
function RankedCard({ report }: { report: Report }) {
  const t = useTokens();
  const defaultMetric = report.metrics.find((m) => m.type === 'money') || report.metrics[0];
  const [active, setActive] = useState(defaultMetric?.name || '');
  const metric = report.metrics.find((m) => m.name === active) || defaultMetric;
  if (!metric || report.rows.length === 0) return <Empty />;
  const dim = report.dimension!;
  const limit = 10;
  const items = report.rows
    .map((r) => ({ label: humanize(String(r[dim.name] ?? '')), value: toNumber(r[metric.name]) ?? 0 }))
    .sort((a, b) => b.value - a.value);
  const shown = items.slice(0, limit);
  const rest = items.slice(limit);
  if (rest.length > 0 && !NOT_ADDITIVE.test(metric.name)) {
    shown.push({ label: `Other (${rest.length})`, value: rest.reduce((s, x) => s + x.value, 0) });
  }
  const total = sumMetric(report, metric);
  const data = {
    labels: shown.map((s) => (s.label.length > 22 ? `${s.label.slice(0, 21)}…` : s.label)),
    datasets: [{
      data: shown.map((s) => s.value),
      backgroundColor: shown.map((s, i) => (i === 0 && !s.label.startsWith('Other (') ? PALETTE[0] : 'rgba(37, 99, 235, 0.45)')),
      borderRadius: 5,
      maxBarThickness: 18,
    }],
  };
  const options = baseOptions(metric, report.currency) as ChartOptions<'bar'>;
  options.indexAxis = 'y';
  options.scales = {
    x: { beginAtZero: true, grid: { color: GRID }, border: { display: false }, ticks: { color: MUTED, font: { size: 11 }, maxTicksLimit: 5, callback: (v) => formatValue(metric, v, report.currency, true) } },
    y: { grid: { display: false }, border: { display: false }, ticks: { color: INK, font: { size: 11.5 }, autoSkip: false } },
  };
  const height = Math.max(120, shown.length * 28 + 30);
  return (
    <div>
      <MetricChips metrics={report.metrics} active={metric.name} onPick={setActive} />
      {total != null && (
        <p className={`mb-2 text-[22px] font-semibold leading-tight tabular-nums ${t.strong}`}>
          {formatValue(metric, total, report.currency)} <span className={`text-[12px] font-normal ${t.muted}`}>total · {items.length} {dim.label.toLowerCase()}{items.length === 1 ? '' : 's'}</span>
        </p>
      )}
      <div style={{ height }}><Bar data={data} options={options} /></div>
    </div>
  );
}

function Picture({ report }: { report: Report }) {
  if (!report.dimension) return <KpiCard report={report} />;
  const name = report.dimension.name;
  if (TIME_DIMENSIONS.has(name)) return <TimeSeriesCard report={report} />;
  if (ORDINAL_DIMENSIONS.has(name)) return <OrdinalCard report={report} />;
  if (SHARE_DIMENSIONS.has(name) && report.rows.length <= 8) return <ShareCard report={report} />;
  return <RankedCard report={report} />;
}

// ── Deck ─────────────────────────────────────────────────────────────────────

type Block = { kind: 'compare'; key: string; reports: Report[] } | { kind: 'single'; key: string; report: Report };

/** Ungrouped cards with the same dataset and metrics but different windows become one comparison. */
function blocks(reports: Report[]): Block[] {
  const out: Block[] = [];
  const groups = new Map<string, Report[]>();
  const order: string[] = [];
  for (const r of reports) {
    const key = r.dimension ? `single:${r.id}` : `${r.dataset}|${r.metrics.map((m) => m.name).join(',')}`;
    if (!groups.has(key)) { groups.set(key, []); order.push(key); }
    groups.get(key)!.push(r);
  }
  for (const key of order) {
    const list = groups.get(key)!;
    const windows = new Set(list.map((r) => `${r.since}|${r.until}|${r.asOf}`));
    if (list.length >= 2 && windows.size >= 2 && !list[0].dimension) {
      out.push({ kind: 'compare', key: `compare:${list[0].id}`, reports: list });
    } else {
      for (const r of list) out.push({ kind: 'single', key: r.id, report: r });
    }
  }
  return out;
}

function CompareTable({ reports }: { reports: Report[] }) {
  const t = useTokens();
  const ordered = [...reports].sort((a, b) => (a.since || '').localeCompare(b.since || ''));
  const base = ordered[0];
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="w-full min-w-[280px] border-collapse text-[13px]">
        <thead>
          <tr className={`text-left text-[11.5px] uppercase tracking-wide ${t.muted}`}>
            <th className="px-1 pb-1.5 font-medium">Metric</th>
            {ordered.map((r) => <th key={r.id} className="px-1 pb-1.5 text-right font-medium">{windowLabel(r, true)}</th>)}
          </tr>
        </thead>
        <tbody>
          {base.metrics.map((m) => (
            <tr key={m.name} className={`${t.row}`}>
              <td className={`px-1 py-1.5 ${t.text}`}>{m.label}</td>
              {ordered.map((r) => <td key={r.id} className={`px-1 py-1.5 text-right font-semibold tabular-nums ${t.strong}`}>{formatValue(m, r.rows[0]?.[m.name], r.currency)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportDeck({
  cards,
  theme = 'dark',
  wide = false,
}: {
  cards: ReportCardSource[];
  /** dark for the bot chat, light for the Control Centre. */
  theme?: DeckTheme;
  /** Let the cards fill the container instead of the chat's 560px column. */
  wide?: boolean;
}) {
  const reports = useMemo(() => cards.map(parseReport), [cards]);
  const items = useMemo(() => blocks(reports), [reports]);
  // On the wide dashboard the cards sit two up; a lone card, a line over time,
  // or a long ranking takes the whole row so the picture has room.
  const spansRow = (block: Block) => items.length === 1
    || (block.kind === 'single' && !!block.report.dimension
      && (TIME_DIMENSIONS.has(block.report.dimension.name) || block.report.rows.length > 6));
  return (
    <DeckThemeContext.Provider value={theme}>
    <div className="flex min-w-0 justify-start">
      <div className={`min-w-0 w-full ${wide ? 'grid grid-cols-1 gap-3 lg:grid-cols-2' : 'max-w-[560px] space-y-2'}`}>
        {items.map((block) => (
          <div key={block.key} className={`min-w-0 ${wide && spansRow(block) ? 'lg:col-span-2' : ''}`}>
            {block.kind === 'compare' ? (
              <Shell
                formula={block.reports.map((r) => r.formula).join('\n')}
                title={`${DATASET_LABEL[block.reports[0].dataset] || block.reports[0].dataset} · ${[...block.reports].sort((a, b) => (a.since || '').localeCompare(b.since || '')).map((r) => windowLabel(r, true)).join(' vs ')}`}
                table={<CompareTable reports={block.reports} />}
              >
                <CompareCard reports={block.reports} />
              </Shell>
            ) : (
              <Shell formula={block.report.formula} title={reportTitle(block.report)} table={<RowsTable report={block.report} />}>
                <Picture report={block.report} />
              </Shell>
            )}
          </div>
        ))}
      </div>
    </div>
    </DeckThemeContext.Provider>
  );
}
